import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GetUserInfoResponse } from '@shared/services/get-user-info.service';
import { loginUser, sendCodeByEmail, verifyCode } from './auth-actions';

interface AuthState {
  userInfo: Partial<GetUserInfoResponse> | null;
  loading: boolean;
  userToken: string | null;
  error: null | string;
  success: boolean;
}

const initialState: AuthState = {
  loading: false,
  error: null,
  success: false,
  userInfo: null,
  userToken: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: state => {
      state.loading = false;
      state.userInfo = null;
      state.userToken = null;
      state.error = null;
      state.success = false;
    },
    setUserInfo: (
      state,
      action: PayloadAction<Partial<GetUserInfoResponse>>,
    ) => {
      state.userInfo = {
        ...action.payload,
      };
    },
    acceptTermsOfUse: state => {
      state.userInfo = {
        ...state.userInfo,
        termsOfUseAccepted: true,
      };
    },
    acceptUpdatedProfile: state => {
      state.userInfo = {
        ...state.userInfo,
        updatedData: true,
      };
    },
  },
  extraReducers(builder) {
    builder.addCase(loginUser.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUser.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.success = true;

      if (payload?.access_token) {
        state.userToken = payload.access_token;
        state.userInfo = payload.userInfo;
      }
    });
    builder.addCase(loginUser.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload as string;
    });

    builder.addCase(verifyCode.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(verifyCode.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.success = true;

      if (payload.access_token) {
        state.userToken = payload.access_token;
        state.userInfo = payload.userInfo;
      }
    });
    builder.addCase(verifyCode.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload as string;
    });

    builder.addCase(sendCodeByEmail.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(sendCodeByEmail.fulfilled, state => {
      state.loading = false;
      state.success = true;
    });
    builder.addCase(sendCodeByEmail.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload as string;
    });
  },
});

export const { logout, setUserInfo, acceptTermsOfUse, acceptUpdatedProfile } =
  authSlice.actions;
export default authSlice.reducer;
